import { lockScroll, unlockScroll } from '@theme/utilities';
/* The theme keeps ownership of open/close, inert, overflow, and Escape. */
class BotanicMenuController extends HTMLElement {
  #abort;
  #observer;
  #menu;
  #generation = 0;

  async connectedCallback() {
    const generation = ++this.#generation;
    await customElements.whenDefined('header-menu');
    if (!this.isConnected || generation !== this.#generation) return;
    this.#menu = this.previousElementSibling;
    if (!this.#menu?.matches('header-menu.botanic-header-menu')) return;
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    const parentLink = (event) => {
      const link = event.target.closest?.('a.menu-list__link[ref="menuitem"]');
      return link && this.#menu.contains(link) && link.closest('.menu-list__list-item')?.querySelector('[ref="submenu[]"]') ? link : null;
    };
    this.#menu.addEventListener('focusin', (event) => {
      if (parentLink(event)) this.#menu.activate(event);
    }, options);
    this.#menu.addEventListener('click', (event) => {
      const link = parentLink(event);
      if (link?.getAttribute('href') === '#') {
        event.preventDefault();
        this.#menu.activate(event);
      }
    }, options);
    this.#observer = new MutationObserver(() => this.#syncExpanded());
    this.#observer.observe(this.#menu, { subtree: true, attributes: true, attributeFilter: ['aria-expanded'] });
    this.#syncExpanded();
  }

  #syncExpanded() {
    for (const button of this.#menu.querySelectorAll('[ref="disclosure[]"]')) {
      const link = button.closest('.menu-list__list-item')?.querySelector('a[ref="menuitem"]');
      if (!link) continue;
      const value = button.getAttribute('aria-expanded') || 'false';
      link.setAttribute('aria-haspopup', 'true');
      if (link.getAttribute('aria-expanded') !== value) link.setAttribute('aria-expanded', value);
    }
  }

  disconnectedCallback() {
    ++this.#generation;
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#menu = null;
  }
}
if (!customElements.get('botanic-menu-controller')) customElements.define('botanic-menu-controller', BotanicMenuController);

/* Place the original-style drawer below the actual sticky header, including
   any announcement above it. The native drawer still owns focus and closing. */
class BotanicDrawerController extends HTMLElement {
  #abort;
  #resize;
  #drawer;
  #header;
  #frame;
  #generation = 0;
  async connectedCallback() {
    const generation = ++this.#generation;
    await customElements.whenDefined('header-drawer');
    if (!this.isConnected || generation !== this.#generation) return;
    this.#drawer = this.previousElementSibling;
    if (!this.#drawer?.matches('header-drawer[data-botanic-menu="true"]')) return;
    this.#header = this.#drawer.closest('header-component');
    if (!this.#header) return;
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal, passive: true };
    this.#resize = new ResizeObserver(() => this.#schedule());
    this.#resize.observe(this.#header);
    window.addEventListener('resize', () => { this.#schedule(); this.#syncState(); }, options);
    window.addEventListener('scroll', () => { if (this.#drawer.isOpen) this.#schedule(); }, options);
    this.#drawer.querySelector(':scope > details').addEventListener('toggle', () => { this.#schedule(); this.#syncState(); }, options);
    this.#schedule();
    this.#syncState();
  }
  #syncState() {
    if (this.#drawer?.isOpen && innerWidth < 990) lockScroll(this);
    else unlockScroll(this);
    if (this.#drawer?.isOpen && innerWidth >= 990) this.#drawer.close();
  }
  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      if (!this.isConnected || !this.#drawer || !this.#header) return;
      const bottom = Math.max(0, Math.min(innerHeight, this.#header.getBoundingClientRect().bottom));
      this.#drawer.style.setProperty('--botanic-header-bottom', `${bottom}px`);
    });
  }
  disconnectedCallback() {
    ++this.#generation;
    this.#abort?.abort();
    unlockScroll(this);
    this.#resize?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#drawer?.style.removeProperty('--botanic-header-bottom');
    this.#drawer = null;
    this.#header = null;
  }
}
if (!customElements.get('botanic-drawer-controller')) customElements.define('botanic-drawer-controller', BotanicDrawerController);
